import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Stack,
  Alert,
  TextField,
  MenuItem,
  IconButton,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "src/redux/store";
import {
  getPrinters,
  AddPrinter,
  UpdatePrinter,
  deletePrinter,
} from "src/redux/slices/printer";

// ----------------------------------------------------------------------
//   Step 4 · Impresoras. Formulario de CREACIÓN + EDICIÓN inline manual
//   (2026-08-11 iter2): el escaneado LAN requiere Worker instalado en
//   la red, algo que el user recién onboarding aún no tiene. Aquí damos
//   de alta manualmente y permitimos editar/borrar cada impresora sin
//   salir del wizard — mismo paradigma que StepMesas (alta + edit + del
//   antes y después de guardar el step). El Worker se configura más
//   tarde desde Ajustes.
// ----------------------------------------------------------------------

const UBICACIONES = [
  { value: "cocina", label: "Cocina" },
  { value: "barra", label: "Barra" },
  { value: "caja", label: "Caja / Tickets" },
  { value: "otra", label: "Otra" },
];

const ANCHOS_PAPEL = [
  { value: 58, label: "58 mm (pequeño)" },
  { value: 80, label: "80 mm (estándar)" },
];

const EMPTY_FORM = {
  name: "",
  location: "cocina",
  printerIp: "",
  width: 80,
  isPrinterNetwork: true,
};

export default function StepImpresoras({ onComplete, onSkip }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  // Slice name: `printers` (plural) — ver `slices/printer.js` línea
  // `name: 'printers'`. Antes leía de `s.printer` (singular) y siempre
  // devolvía undefined → la lista aparecía vacía y no se podía editar.
  const printers = useSelector((s) => s.printers?.printers || []);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = modo alta
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    dispatch(getPrinters());
  }, [dispatch]);

  const handleField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleStartEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      location: p.location || "cocina",
      printerIp: p.printerIp || "",
      width: Number(p.width) || 80,
      isPrinterNetwork: !!p.isPrinterNetwork,
    });
    // Scroll suave al formulario para que el user vea que entró en modo
    // edición (el form vive debajo de la lista y en móvil puede quedar
    // fuera de viewport).
    try {
      const el = document.getElementById("wizard-printer-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch { /* noop */ }
  };

  const handleSubmit = async () => {
    const name = (form.name || "").trim();
    const ip = (form.printerIp || "").trim();
    if (!name) {
      enqueueSnackbar("Ponle un nombre a la impresora", { variant: "warning" });
      return;
    }
    if (form.isPrinterNetwork && !ip) {
      enqueueSnackbar("Introduce la IP de la impresora de red", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        location: form.location,
        printerIp: form.isPrinterNetwork ? ip : "",
        width: Number(form.width) || 80,
        isPrinterNetwork: !!form.isPrinterNetwork,
      };
      if (editingId != null) {
        await dispatch(UpdatePrinter({ ...payload, id: editingId }));
        enqueueSnackbar(`Impresora "${name}" actualizada`, { variant: "success" });
      } else {
        await dispatch(AddPrinter(payload));
        enqueueSnackbar(`Impresora "${name}" añadida`, { variant: "success" });
      }
      resetForm();
      await dispatch(getPrinters());
    } catch (err) {
      enqueueSnackbar(
        err?.message || (editingId != null ? "Error actualizando impresora" : "Error añadiendo impresora"),
        { variant: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (p) => {
    try {
      await dispatch(deletePrinter(p.id));
      await dispatch(getPrinters());
      // Si estábamos editando la impresora que se acaba de borrar,
      // volvemos al modo alta con el form vacío.
      if (editingId === p.id) resetForm();
    } catch (err) {
      enqueueSnackbar(err?.message || "No se pudo eliminar", { variant: "error" });
    }
  };

  const handleContinue = async () => {
    await onComplete({
      dataJson: JSON.stringify({ printersConfigured: printers.length }),
    });
  };

  const isEditing = editingId != null;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Configura tus impresoras
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        Añade las impresoras que usarás para comandas y tickets. Puedes
        editarlas o eliminarlas en cualquier momento. Si tienes impresoras
        de red (Ethernet/WiFi), introduce su IP; el escaneo automático de
        la red requiere el Worker de escritorio, que podrás instalar más
        tarde desde Ajustes.
      </Typography>

      {printers.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Ya tienes {printers.length} impresora{printers.length === 1 ? "" : "s"} configurada{printers.length === 1 ? "" : "s"}. Puedes seguir añadiendo o continuar.
        </Alert>
      )}

      {/* Listado de impresoras ya creadas — cada una editable/borrable
          inline sin salir del step. */}
      {printers.length > 0 && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {printers.map((p) => {
            const isThisEditing = editingId === p.id;
            return (
              <Card
                key={p.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  borderColor: isThisEditing ? "primary.main" : "divider",
                  borderWidth: isThisEditing ? 2 : 1,
                  backgroundColor: isThisEditing ? "primary.lighter" : "background.paper",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                  <PrintRoundedIcon color={isThisEditing ? "primary" : "action"} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700} noWrap>
                      {p.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {p.isPrinterNetwork ? (p.printerIp || "sin IP") : "USB local"}
                      {" · "}{p.location || "sin ubicación"}
                      {" · "}{p.width || 80} mm
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => handleStartEdit(p)}
                    aria-label="Editar"
                    color={isThisEditing ? "primary" : "default"}
                  >
                    <EditRoundedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(p)}
                    aria-label="Eliminar"
                    sx={{ color: "error.main" }}
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      <Divider sx={{ my: 2 }}>
        <Typography variant="overline" color="text.secondary">
          {isEditing ? `Editando: ${form.name || "impresora"}` : "Añadir impresora"}
        </Typography>
      </Divider>

      {/* Formulario alta / edición inline */}
      <Card id="wizard-printer-form" variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Nombre"
              placeholder="Ej. Cocina principal"
              value={form.name}
              onChange={handleField("name")}
              fullWidth
              required
            />
            <TextField
              select
              label="Ubicación"
              value={form.location}
              onChange={handleField("location")}
              sx={{ minWidth: 180 }}
            >
              {UBICACIONES.map((u) => (
                <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              label="Tipo"
              value={form.isPrinterNetwork ? "network" : "usb"}
              onChange={(e) => setForm((p) => ({ ...p, isPrinterNetwork: e.target.value === "network" }))}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="network">Impresora de red</MenuItem>
              <MenuItem value="usb">USB / local</MenuItem>
            </TextField>
            <TextField
              label="Dirección IP"
              placeholder="192.168.1.100"
              value={form.printerIp}
              onChange={handleField("printerIp")}
              disabled={!form.isPrinterNetwork}
              fullWidth
              helperText={form.isPrinterNetwork ? "IP local de la impresora en tu red" : "USB no requiere IP"}
            />
            <TextField
              select
              label="Ancho papel"
              value={form.width}
              onChange={handleField("width")}
              sx={{ minWidth: 160 }}
            >
              {ANCHOS_PAPEL.map((w) => (
                <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            {isEditing && (
              <Button
                onClick={resetForm}
                disabled={saving}
                startIcon={<CloseRoundedIcon />}
              >
                Cancelar edición
              </Button>
            )}
            <LoadingButton
              variant="contained"
              color="primary"
              loading={saving}
              onClick={handleSubmit}
              startIcon={isEditing ? <SaveRoundedIcon /> : <AddRoundedIcon />}
            >
              {isEditing ? "Guardar cambios" : "Añadir impresora"}
            </LoadingButton>
          </Stack>
        </Stack>
      </Card>

      <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 4 }}>
        <Button onClick={onSkip} color="inherit">
          Saltar por ahora
        </Button>
        <Button variant="contained" color="success" onClick={handleContinue}>
          Continuar →
        </Button>
      </Stack>
    </Box>
  );
}
